import React, { useState, useEffect } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import Text from '../../../../shared/components/Text/Text';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { setNonDateBasedProgramDays, setSelectedProgramDayNumber } from '../../ducks/calendarActions';

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  padding: 8px;
  padding-bottom: ${(props) => (props.viewType === 'weekView' ? '8px' : '12px')};
  background: white;
  align-items: center;
  gap: 8px;
  overflow-x: scroll;
  min-height: 50px;

  /* Only apply max-width and fixed positioning for weekView */
  ${(props) => props.viewType === 'weekView'
    && `
    max-width: calc(100% - ${props.isPrimaryAnimatedSideBarOpen ? 384 : 0}px - 48px);
    position: fixed;
    bottom: 0;

    @media screen and (max-width: 1023px) {
      max-width: calc(100% - ${props.isPrimaryAnimatedSideBarOpen ? 300 : 0}px - 48px);
    }
  `}
`;

const WeeksWrapper = styled('div')`
  display: flex;
  gap: 8px;
`;

const WeekContainer = styled('div')`
  display: flex;
  padding: 4px 16px;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 4px;
  background: ${(props) => props.isSelected && '#F8F8F8'};
   :hover {
      background: #F8F8F8;
    }
`;

const OpacityWrapper = styled('div')`
display: flex;
 transition: ${TransitionGlobal};
    :hover {
      opacity: 0.7;
    }            
`;

const ProgramLibraryFooter = () => {
  const dispatch = useDispatch();
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const [selectedWeek, setSelectedWeek] = useState(1); // default selected week
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );

  /** Step 1: build a grouped array manually using forEach + find */
  const groupedWeeks = [];
  nonDateBasedProgramItems.forEach((item) => {
    const existingWeek = groupedWeeks.find((w) => w.weekNumber === item.weekNumber);

    if (existingWeek) {
      existingWeek.days.push(item);
    } else {
      groupedWeeks.push({
        weekNumber: item.weekNumber,
        days: [item],
      });
    }
  });

  /** Step 2: sort days in each week */
  const sortedGroupedWeeks = groupedWeeks.map((week) => ({
    ...week,
    days: [...week.days].sort((a, b) => a.dayInWeek - b.dayInWeek),
  }));

  useEffect(() => {
    const dayThatIncludesWeekNumber = nonDateBasedProgramItems.filter(
      (day) => day.dayNumber === selectedProgramDayNumber,
    )[0];
    const weekNumber = dayThatIncludesWeekNumber?.weekNumber || 1;
    setSelectedWeek(weekNumber);
  }, [selectedProgramDayNumber]);

  const addWeek = () => {
    const lastWeek = sortedGroupedWeeks[sortedGroupedWeeks?.length - 1];
    /** prevent user from adding more than 52 weeks */
    if (lastWeek.weekNumber >= 52) return;
    /** 1 Determine the last day in the week */
    const lastDayInWeek = lastWeek.days[lastWeek.days.length - 1];

    /** 2 Create the new day */
    const newDay = {
      dayInWeek: 1,
      dayNumber: lastDayInWeek.dayNumber + 1,
      exercises: [],
      repCount: 0,
      setCount: 0,
      /** add week */
      weekNumber: lastWeek.weekNumber + 1,
    };

    /** 3 Insert new day in the correct position and adjust dayNumbers for all subsequent days */
    const updatedItems = nonDateBasedProgramItems.map((item) => {
    /** If the current item comes after the new day, increment its dayNumber */
      if (item.dayNumber >= newDay.dayNumber) {
        return { ...item, dayNumber: item.dayNumber + 1 };
      }
      return item;
    });

    /** 4 Insert the new day into the array */
    updatedItems.push(newDay);

    /** 5 Sort by dayNumber to maintain proper order */
    updatedItems.sort((a, b) => a.dayNumber - b.dayNumber);

    /** 6 Update Redux */
    /** this resets the whole day array, not just exercise items in a day
     * setNonDateBaedProgramItems - will just set exercise items for a specific day number
     */
    dispatch(setNonDateBasedProgramDays(updatedItems));
  };

  return (
    <Wrapper viewType={viewType} isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}>
      <WeeksWrapper>
        {sortedGroupedWeeks.map((week) => (
          <WeekContainer
            key={week.weekNumber}
            isSelected={selectedWeek === week.weekNumber}
            onClick={() => {
              // setSelectedWeek(week.weekNumber);
              const firstDayInClickedWeek = week.days.filter((day) => day.dayInWeek === 1);
              const { dayNumber } = firstDayInClickedWeek[0];
              dispatch(setSelectedProgramDayNumber(dayNumber));
            }}
          >
            <Text fontSize='16px' fontWeight={selectedWeek === week.weekNumber ? 700 : 400} color={selectedWeek !== week.weekNumber && '#9E9E9E'}>
              Week
            </Text>
            <Text fontSize='16px' fontWeight={selectedWeek === week.weekNumber ? 700 : 400} color={selectedWeek !== week.weekNumber && '#9E9E9E'}>
              {week.weekNumber}
            </Text>
            <IcomoonReact
              iconSet={iconSet}
              size={10}
              icon='down-arrow'
              color={selectedWeek !== week.weekNumber ? '#9E9E9E' : '#444444'}
            />
          </WeekContainer>
        ))}
      </WeeksWrapper>
      <OpacityWrapper>
        <Text
          fontSize='16px'
          color='#77A7FF'
          style={{
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          onClick={() => {
            addWeek();
          }}
        >
          + Add Week
        </Text>
      </OpacityWrapper>
    </Wrapper>
  );
};

export default ProgramLibraryFooter;
